import React, { useState, useEffect } from 'react';
import {
  TextField,
  Grid,
  Box,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  AgronodDialog,
  AgronodButton as Button,
  AgronodSwitch,
  AgronodTypography as Typography,
  AgronodAlert as Alert,
} from '@agronod/mui-components';
import { Issuer, UpdateIssuerRequest } from '@/types';
import IssuerLogoField from '@/components/admin/IssuerLogoField';

interface EditIssuerModalProps {
  open: boolean;
  issuer: Issuer | null;
  onClose: () => void;
  onSubmit: (id: string, data: UpdateIssuerRequest) => Promise<void>;
  isLoading?: boolean;
}

/**
 * Modal for editing existing issuer details.
 *
 * Follows same pattern as EditControlTypeModal:
 * - Pre-populates form with existing issuer data
 * - Validates all fields before submission
 * - Shows metadata (createdAt, updatedAt) as read-only info
 * - Includes isActive toggle for activation/deactivation
 */
const EditIssuerModal: React.FC<EditIssuerModalProps> = ({
  open,
  issuer,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<UpdateIssuerRequest>({
    code: '',
    displayName: '',
    description: '',
    website: '',
    logoUrl: '',
    contactEmail: '',
    contactPhone: '',
    isActive: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens or issuer changes
  useEffect(() => {
    if (issuer && open) {
      setFormData({
        code: issuer.code,
        displayName: issuer.displayName,
        description: issuer.description || '',
        website: issuer.website || '',
        logoUrl: issuer.logoUrl || '',
        contactEmail: issuer.contactEmail || '',
        contactPhone: issuer.contactPhone || '',
        isActive: issuer.isActive,
      });
      setErrors({});
    }
  }, [issuer, open]);

  /**
   * Validates form fields according to business rules.
   *
   * Rules from blueprint:
   * - Code: required, 1-50 chars
   * - DisplayName: required, 1-255 chars
   * - Description: max 2000 chars
   * - Website: valid URL format if provided
   * - LogoUrl: valid URL format if provided
   * - ContactEmail: valid email format if provided
   * - ContactPhone: max 50 chars
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Code validation
    if (!formData.code?.trim()) {
      newErrors['code'] = 'Kod är obligatorisk';
    } else if (formData.code.trim().length > 50) {
      newErrors['code'] = 'Kod får max vara 50 tecken';
    }

    // DisplayName validation
    if (!formData.displayName?.trim()) {
      newErrors['displayName'] = 'Visningsnamn är obligatoriskt';
    } else if (formData.displayName.trim().length > 255) {
      newErrors['displayName'] = 'Visningsnamn får max vara 255 tecken';
    }

    // Description validation
    if (formData.description && formData.description.length > 2000) {
      newErrors['description'] = 'Beskrivning får max vara 2000 tecken';
    }

    // Website URL validation
    if (formData.website && formData.website.trim()) {
      const urlPattern = /^https?:\/\/.+\..+/;
      if (!urlPattern.test(formData.website.trim())) {
        newErrors['website'] = 'Webbplats måste vara en giltig URL (t.ex. https://example.com)';
      }
    }

    // ContactEmail validation
    if (formData.contactEmail && formData.contactEmail.trim()) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(formData.contactEmail.trim())) {
        newErrors['contactEmail'] = 'Kontakt-e-post måste vara en giltig e-postadress';
      }
    }

    // ContactPhone validation
    if (formData.contactPhone && formData.contactPhone.length > 50) {
      newErrors['contactPhone'] = 'Kontakttelefon får max vara 50 tecken';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!issuer || !validateForm()) {
      return;
    }

    try {
      // Build update payload - send all fields, required fields must have values
      const submitData: UpdateIssuerRequest = {
        code: formData.code?.trim() || '',
        displayName: formData.displayName?.trim() || '',
        description: formData.description?.trim() || null,
        website: formData.website?.trim() || null,
        logoUrl: formData.logoUrl?.trim() || null,
        contactEmail: formData.contactEmail?.trim() || null,
        contactPhone: formData.contactPhone?.trim() || null,
        isActive: formData.isActive ?? true,
      };

      await onSubmit(issuer.id, submitData);
      handleClose();
    } catch (error) {
      console.error('Error updating issuer:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      code: '',
      displayName: '',
      description: '',
      website: '',
      logoUrl: '',
      contactEmail: '',
      contactPhone: '',
      isActive: true,
    });
    setErrors({});
    onClose();
  };

  const handleChange = (field: keyof UpdateIssuerRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'isActive' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  if (!issuer) {
    return null;
  }

  // Format dates for display
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <AgronodDialog
      open={open}
      onClose={handleClose}
      title={`Redigera issuer - ${issuer.code}`}
      maxWidth="md"
      fullWidth
      disableRestoreFocus
      slotProps={{
        paper: {
          sx: { minHeight: '600px' }
        }
      }}
      actions={
        <>
          <Button
            onClick={handleClose}
            disabled={isLoading}
          >
            Avbryt
          </Button>
          <Button
            type="submit"
            form="edit-issuer-form"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? 'Sparar...' : 'Spara ändringar'}
          </Button>
        </>
      }
    >
      <form id="edit-issuer-form" onSubmit={handleSubmit}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {/* Code field */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Kod"
                value={formData.code}
                onChange={handleChange('code')}
                error={!!errors['code']}
                helperText={errors['code'] || 'Unik identifierare (max 50 tecken)'}
                required
              />
            </Grid>

            {/* Display Name field */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Visningsnamn"
                value={formData.displayName}
                onChange={handleChange('displayName')}
                error={!!errors['displayName']}
                helperText={errors['displayName'] || 'Namn som visas i systemet (max 255 tecken)'}
                required
              />
            </Grid>

            {/* Description field */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Beskrivning"
                value={formData.description}
                onChange={handleChange('description')}
                error={!!errors['description']}
                helperText={errors['description'] || 'Valfri beskrivning (max 2000 tecken)'}
                multiline
                rows={3}
              />
            </Grid>

            {/* Website field */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Webbplats"
                value={formData.website}
                onChange={handleChange('website')}
                error={!!errors['website']}
                helperText={errors['website'] || 'Issuer-webbplats (t.ex. https://example.com)'}
                placeholder="https://example.com"
              />
            </Grid>

            {/* Logo upload */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <IssuerLogoField
                value={formData.logoUrl ?? null}
                onChange={(v) => setFormData((prev) => ({ ...prev, logoUrl: v }))}
                disabled={isLoading}
              />
            </Grid>

            {/* Contact Email field */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Kontakt-e-post"
                value={formData.contactEmail}
                onChange={handleChange('contactEmail')}
                error={!!errors['contactEmail']}
                helperText={errors['contactEmail'] || 'Kontakt-e-postadress'}
                placeholder="kontakt@example.com"
                type="email"
              />
            </Grid>

            {/* Contact Phone field */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Kontakttelefon"
                value={formData.contactPhone}
                onChange={handleChange('contactPhone')}
                error={!!errors['contactPhone']}
                helperText={errors['contactPhone'] || 'Kontakttelefonnummer (max 50 tecken)'}
                placeholder="+46 XX XXX XX XX"
              />
            </Grid>

            {/* isActive toggle */}
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <AgronodSwitch
                    checked={formData.isActive || false}
                    onChange={handleChange('isActive')}
                  />
                }
                label="Aktiv"
              />
            </Grid>

            {/* Metadata display */}
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                <Typography variant="body2" component="div">
                  <strong>Skapad:</strong> {formatDate(issuer.createdAt)}
                </Typography>
                <Typography variant="body2" component="div">
                  <strong>Senast uppdaterad:</strong> {formatDate(issuer.updatedAt)}
                </Typography>
                {issuer._count && issuer._count.controlTypes > 0 && (
                  <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                    Denna issuer har {issuer._count.controlTypes} associerade kontrolltyper.
                  </Typography>
                )}
              </Alert>
            </Grid>
          </Grid>
        </Box>
      </form>
    </AgronodDialog>
  );
};

export default EditIssuerModal;
